import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#8CB89F] to-[#7aa88f] text-white py-16">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold">Terms of Service</h1>
          <p className="mt-2 text-white/80">Last updated: February 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-8">
          
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              By accessing or using the services provided by Aston Lily Shutters ("Company", "we", "our", or "us"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Services</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              Aston Lily Shutters provides:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Custom plantation shutters and window coverings</li>
              <li>Free home surveys and measurements</li>
              <li>Professional installation services</li>
              <li>DIY delivery service (for selected products)</li>
              <li>Online quote calculator</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Quotes and Pricing</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              Please note the following regarding our quotes and pricing:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Online quotes are estimates based on the measurements you provide</li>
              <li>Final pricing is confirmed during the free home survey</li>
              <li>Quotes are valid for 30 days from the date of issue</li>
              <li>Prices include VAT unless otherwise stated</li>
              <li>We reserve the right to adjust pricing if there are significant changes to the scope of work</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Orders and Payment</h2>
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Deposits</h3>
            <p className="text-gray-600 leading-relaxed">
              A deposit of 50% is required to confirm your order. The remaining balance is due upon completion of installation.
            </p>
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Payment Methods</h3>
            <p className="text-gray-600 leading-relaxed">
              We accept payment by bank transfer, debit card, and credit card. Payment plans may be available – please ask for details.
            </p>
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Cancellations</h3>
            <p className="text-gray-600 leading-relaxed">
              You may cancel your order within 14 days of placing it for a full refund of your deposit. After this period, or once manufacturing has commenced, deposits are non-refundable.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Warranty</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              All our shutters come with a comprehensive warranty:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li><strong>Budget Range:</strong> 10 Year Structural Warranty</li>
              <li><strong>Polymer, Paulownia+, Basswood, UK Made Basswood & Aluminium:</strong> 10 Year Structural Warranty</li>
              <li>Covers manufacturing defects, structural integrity, and installation workmanship</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-3">
              The warranty does not cover damage caused by misuse, accidents, or normal wear and tear.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Installation</h2>
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Full Service Installation</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Installation is carried out by our trained professionals</li>
              <li>We will contact you to arrange a convenient installation date</li>
              <li>Please ensure clear access to the installation area</li>
              <li>Installation typically takes 1-2 hours per window</li>
            </ul>
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">DIY Delivery</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Shutters are delivered fully assembled with all necessary fixings</li>
              <li>Detailed installation instructions are included</li>
              <li>Video tutorials are available online</li>
              <li>100% guaranteed to fit when measured by our experts</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Delivery</h2>
            <p className="text-gray-600 leading-relaxed">
              Manufacturing and delivery times are typically 10-12 weeks from order confirmation. We will keep you informed of progress and confirm delivery/installation dates in advance. Delays may occur due to circumstances beyond our control.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Customer Responsibilities</h2>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Provide accurate information and measurements where applicable</li>
              <li>Ensure the property is accessible for surveys and installation</li>
              <li>Remove any window dressings or obstructions before installation</li>
              <li>Notify us of any changes to contact details or requirements</li>
              <li>Make payments as agreed</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-600 leading-relaxed">
              To the maximum extent permitted by law, Aston Lily Shutters shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, use, goodwill, or other intangible losses resulting from your use of our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Intellectual Property</h2>
            <p className="text-gray-600 leading-relaxed">
              All content on our website, including text, graphics, logos, images, and software, is the property of Aston Lily Shutters and is protected by copyright and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Dispute Resolution</h2>
            <p className="text-gray-600 leading-relaxed">
              In the event of any dispute, we encourage you to contact us first to seek an amicable resolution. If a dispute cannot be resolved informally, it shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. Your continued use of our services after such changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Governing Law</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Contact Us</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700"><strong>Aston Lily Shutters</strong></p>
              <p className="text-gray-600 mt-2">Email: info@astonlily.co.uk</p>
              <p className="text-gray-600">Phone: 0117 123 4567</p>
              <p className="text-gray-600">WhatsApp: +44 7378 415338</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
